// src/lib/constants/storeAppCheck.ts
//
// The store app id check (appfactory ADR-0029): after somebody saves a store's
// app id on a platform tab, the build farm asks the store whether that id
// exists in the developer account the app publishes from, and the platform's
// Status tab shows the verdict as ONE line under that store's account. Only a
// platform listed here has a check; every other platform never asks.

import { APP_FACTORY_QUERY_KEYS } from './appFactory';
import { APP_PLATFORM_INFO, type AppPlatformKey } from './appPlatform';

export interface StoreAppCheckInfo {
  /** The factory platform key = the `platform` parameter of the read. */
  platform: AppPlatformKey;
  /** The store's name in a sentence ("Amazon"). */
  storeName: string;
  /** Where the holder finds the id ("Amazon developer console"). */
  consoleName: string;
}

/** Platforms whose saved store app id the farm checks (Fire TV first). */
export const STORE_APP_CHECK_INFO: Partial<Record<string, StoreAppCheckInfo>> = {
  [APP_PLATFORM_INFO.firetv.key]: {
    platform: APP_PLATFORM_INFO.firetv.key,
    storeName: 'Amazon',
    consoleName: 'Amazon developer console',
  },
};

/** The daemon's states (FactoryStoreAppCheck.state). */
export const STORE_APP_CHECK_STATE = {
  NONE: 'none',
  NOT_APPLICABLE: 'not_applicable',
  PENDING: 'pending',
  VERIFIED: 'verified',
  NOT_FOUND: 'not_found',
  UNAUTHORIZED: 'unauthorized',
  UNREACHABLE: 'unreachable',
} as const;

/** While a check is in flight the line re-reads, briefly: a later visit re-reads anyway. */
export const STORE_APP_CHECK_POLL = {
  INTERVAL_MS: 5_000,
  MAX_MS: 60_000,
} as const;

export const STORE_APP_CHECK_QUERY_KEYS = {
  /** The saved id is part of the key: a changed id is a new question, never the old answer. */
  check: (cId: number, appCfgId: number, platform: string, storeAppId: string) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'storeAppCheck', cId, appCfgId, platform, storeAppId] as const,
} as const;

/** What the copy names: the id's label on the platform tab + the store's two names. */
export interface StoreAppCheckCopyFacts {
  idLabel: string;
  storeName: string;
  consoleName: string;
}

export const STORE_APP_CHECK_COPY = {
  verified: (f: StoreAppCheckCopyFacts) => `${f.idLabel} confirmed with ${f.storeName}`,
  checking: (f: StoreAppCheckCopyFacts) => `Checking the ${f.idLabel} with ${f.storeName}…`,
  farmOffline: () => 'The check runs when the build farm is back online.',
  notFound: (f: StoreAppCheckCopyFacts) =>
    `${f.storeName} does not know this App ID in the developer account this app publishes from.`,
  notFoundOperator: (f: StoreAppCheckCopyFacts) =>
    `Compare it with the app's page in the ${f.consoleName}.`,
  notFoundHolder: (supportContact: string) => `Contact ${supportContact} to correct it.`,
  /** Operator only. `account` = the store account's label, '' when it has none. */
  unauthorized: (f: StoreAppCheckCopyFacts, account: string) =>
    `${f.storeName} refused the API credentials of the developer account${account ? ` “${account}”` : ''}. ` +
    'Re-attach the security profile to the App Submission API, or re-seal the API credentials.',
  unreachable: (f: StoreAppCheckCopyFacts) =>
    `${f.storeName} could not be reached; the check is retried.`,
} as const;
